/**
 * Admin Portal → Codes → Overview: the nightly codes reconcile (Codes PR-9) — the pure part.
 *
 * The server (routers/admin_codes_reconcile.py, services/codes_reconcile.py) runs eight checks across
 * every code family each night and on "Run reconcile now"; the Overview shows the last run and offers
 * the button to admin roles. Every word here is derived from the server's answer; nothing is stored.
 *
 * Web-only (the admin SPA).
 */

import { CODES_API } from './codesAdminLogic';

export const RECONCILE_API = `${CODES_API}/reconcile`;

export interface ReconcileCheck { check: string; status: string | null; findings: number }

export interface ReconcileRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  by: string;                        // 'cron' | 'admin' — never an id
  status: string | null;             // 'running' while unfinished
  findings: number;
  alerts_raised: number;
  alerts_resolved: number;
  duration_ms: number | null;
  checks: ReconcileCheck[];
}

export interface ReconcileRuns {
  schema_pending: boolean;
  runs: ReconcileRun[];
  last?: ReconcileRun | null;
  running?: boolean;
  open_alerts: number;
  switches?: { codes_reconcile?: boolean; inferred_redemption?: boolean };
}

/** The checks in the server's order, in an admin's words. */
export const RECONCILE_CHECK_LABELS: Record<string, string> = {
  namespace: 'Code names',
  counters: 'Use counters',
  batch_imports: 'App Store batch counts',
  store_offers: 'Store offers vs discounts',
  tag_combos: 'Tag discounts on sale everywhere',
  reward_inventory: 'Summer50 reward codes',
  quarter_quota: 'App Store quarter quota',
  inferred_redemptions: 'Inferred App Store redemptions',
};

export const RECONCILE_STATUS_LABELS: Record<string, string> = {
  running: 'Running…',
  ok: 'All clear',
  findings: 'Found something',
  partial: 'Partly finished',
  failed: 'Failed',
  timeout: 'Cut off (took too long)',
  cancelled: 'Stopped (a deploy restarted the server)',
  abandoned: 'Never finished',
  stale: 'Never finished',
};

export function reconcileStatusLabel(status: string | null | undefined): string {
  return (status && RECONCILE_STATUS_LABELS[status]) || (status ?? 'Unknown');
}

export function reconcileTone(status: string | null | undefined): 'success' | 'warning' | 'error' | 'info' {
  if (status === 'ok') return 'success';
  if (status === 'running') return 'info';
  if (status === 'findings' || status === 'partial' || status === 'timeout') return 'warning';
  return 'error';
}

/** "Last run: All clear · by the nightly job · 3 findings · 2 new alerts". */
export function reconcileSummaryLine(run: ReconcileRun | null | undefined): string {
  if (!run) return 'No reconcile has run yet. The nightly job runs at 08:41 UTC.';
  const who = run.by === 'admin' ? 'started by an admin' : 'by the nightly job';
  const parts = [reconcileStatusLabel(run.status), who];
  if (run.status !== 'running') {
    parts.push(`${run.findings} finding${run.findings === 1 ? '' : 's'}`);
    if (run.alerts_raised) parts.push(`${run.alerts_raised} new alert${run.alerts_raised === 1 ? '' : 's'}`);
    if (run.alerts_resolved) parts.push(`${run.alerts_resolved} resolved`);
  }
  return parts.join(' · ');
}

export function checkLine(c: ReconcileCheck): string {
  const label = RECONCILE_CHECK_LABELS[c.check] ?? c.check;
  if (c.status === 'ok') return `${label}: fine`;
  if (c.status === 'findings') return `${label}: ${c.findings} finding${c.findings === 1 ? '' : 's'}`;
  if (c.status === 'skipped') return `${label}: not checked (not set up yet)`;
  if (c.status === 'error') return `${label}: could not be checked`;
  return `${label}: ${c.status ?? 'unknown'}`;
}

/**
 * Keep polling while a run is in flight (the button's run, or the cron's), never forever. The server
 * cuts a run off at RUN_TIMEOUT_S = 600 s (services/codes_reconcile.py), so the poll lasts that long plus
 * a minute's slack for the finish write (review of #4336, L4 — it stopped at 180 s).
 */
export const RECONCILE_RUN_BOUND_S = 600;
export const RECONCILE_POLL_SLACK_S = 60;
export const RECONCILE_POLL_MS = 5000;
export const RECONCILE_POLL_MAX = Math.ceil(((RECONCILE_RUN_BOUND_S + RECONCILE_POLL_SLACK_S) * 1000) / RECONCILE_POLL_MS);

export function shouldPoll(runs: ReconcileRuns | null | undefined, polls: number): boolean {
  return Boolean(runs?.running) && polls < RECONCILE_POLL_MAX;
}

/** The button's refusals, in plain words. */
export function runReconcileError(status: number, code: string | null, fallback: string): string {
  if (status === 409 || code === 'busy') return 'A reconcile run is already in progress. Its result appears here when it finishes.';
  if (status === 503 && code === 'disabled') return 'The codes reconcile is switched off (FF_CODES_RECONCILE).';
  if (status === 503) return 'The codes reconcile is not set up yet — the deploy may still be finishing.';
  if (status === 403) return 'Only an admin can run the reconcile.';
  return fallback;
}
